/**
 * Environment Configuration Module - Sprint 31
 *
 * Provides automatic detection of the runtime environment (local vs mainnet)
 * and centralizes all environment-specific configuration.
 *
 * Usage:
 * ```typescript
 * import { environment, isMainnet } from './config/environment';
 *
 * if (isMainnet()) {
 *     // Use IC agent
 * } else {
 *     // Use local proxy
 * }
 * ```
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Environment mode
 */
export type EnvironmentMode = 'local' | 'mainnet' | 'staging';

/**
 * Complete environment configuration
 */
export interface ICEnvironment {
    /** Current environment mode */
    mode: EnvironmentMode;

    /** Whether running on mainnet */
    isMainnet: boolean;

    /** Whether to use the local proxy server */
    useProxy: boolean;

    /** Proxy server URL for server-side features (upload, import, LLM, health scan) */
    proxyUrl: string;

    /** IC host URL for agent connections */
    icHost: string;

    /** Local replica host (only used in local mode) */
    localReplicaHost: string;

    /** Internet Identity canister ID */
    internetIdentityCanisterId: string;

    /** Whether debug logging is enabled */
    debug: boolean;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Mainnet domain patterns (suffix match)
 */
const MAINNET_DOMAINS = [
    '.ic0.app',
    '.icp0.io',
    '.raw.ic0.app',
    '.raw.icp0.io'
];

/**
 * Custom domains pointing to ICP mainnet canisters (exact match).
 * These are served by ICP boundary nodes via custom domain registration.
 */
const MAINNET_CUSTOM_DOMAINS = [
    'asd.unboundata.com',
];

/**
 * Staging domain patterns (if you have a staging environment)
 */
const STAGING_DOMAINS = [
    '.staging.ic0.app'
];

/**
 * Cloudflare Tunnel dev domain.
 * Routes through ic_replica_proxy (port 9080) with /proxy/ prefix
 * forwarded to canister_proxy (port 5001).
 * (/api/ is reserved for IC replica's native /api/v2/ endpoints)
 */
const CLOUDFLARE_DEV_DOMAINS = [
    'asd-dev.unboundata.com',
];

/**
 * Local development indicators
 * Includes private network ranges for LAN-based remote access
 * and Cloudflare Tunnel dev domains.
 */
const LOCAL_INDICATORS = [
    'localhost',
    '127.0.0.1',
    '0.0.0.0',
    '192.168.', // Private network
    '10.',      // Private network
    'asd-dev.unboundata.com', // Cloudflare Tunnel → local dev
];

/**
 * Check if the hostname is a Cloudflare Tunnel dev domain.
 */
function isCloudflareDevDomain(hostname: string): boolean {
    return CLOUDFLARE_DEV_DOMAINS.some(d => hostname === d);
}

/**
 * Get the base hostname for dynamic URL construction.
 * Returns the browser's hostname so that remote access
 * correctly targets the host machine instead of 127.0.0.1.
 */
function getBaseHost(): string {
    if (typeof window === 'undefined') return '127.0.0.1';
    const hostname = window.location.hostname;
    // For localhost or empty, use 127.0.0.1
    if (!hostname || hostname === 'localhost') return '127.0.0.1';
    // For canister subdomain hostnames (e.g. vizcg-th777-77774-qaaea-cai.localhost),
    // use 127.0.0.1 since we're on the same machine
    if (hostname.endsWith('.localhost')) return '127.0.0.1';
    return hostname;
}

/**
 * Get the replica port based on access method.
 * Local access uses port 8080 (direct), remote access uses 9080 (reverse proxy).
 * Cloudflare Tunnel uses port 443 (TLS termination by Cloudflare).
 */
function getReplicaPort(): number {
    const host = getBaseHost();
    if (host === '127.0.0.1') return 8080;
    if (isCloudflareDevDomain(host)) return 443;
    return 9080;
}

/**
 * Default configuration values
 * Proxy and replica URLs are dynamically derived from the browser hostname
 * so that remote access works without hardcoded localhost references.
 */
const DEFAULTS = {
    get localProxyUrl(): string {
        const host = getBaseHost();
        if (isCloudflareDevDomain(host)) {
            // Proxy API calls routed through ic_replica_proxy's /proxy/ path
            // (/api/ is reserved for IC replica's native /api/v2/ endpoints)
            return `https://${host}/proxy`;
        }
        return `http://${host}:5001`;
    },
    get localReplicaHost(): string {
        const host = getBaseHost();
        if (isCloudflareDevDomain(host)) {
            return `https://${host}`;
        }
        return `http://${host}:${getReplicaPort()}`;
    },
    mainnetHost: 'https://ic0.app',
    stagingHost: 'https://ic0.app',
    mainnetInternetIdentity: 'rdmx6-jaaaa-aaaaa-aaadq-cai',
    localInternetIdentity: 'rdmx6-jaaaa-aaaaa-aaadq-cai' // Use mainnet II even locally for testing
};

// ============================================================================
// Detection Logic
// ============================================================================

/**
 * Detect the current environment based on hostname
 */
function detectEnvironmentMode(): EnvironmentMode {
    // Check for override in localStorage (useful for testing)
    const override = getLocalStorageOverride();
    if (override) {
        console.log(`🌐 Environment override from localStorage: ${override}`);
        return override;
    }

    // Get current hostname
    const hostname = typeof window !== 'undefined'
        ? window.location.hostname
        : 'localhost';

    // Check for mainnet domains (suffix match + exact match for custom domains)
    if (MAINNET_DOMAINS.some(domain => hostname.endsWith(domain))
        || MAINNET_CUSTOM_DOMAINS.some(domain => hostname === domain)) {
        return 'mainnet';
    }

    // Check for staging domains
    if (STAGING_DOMAINS.some(domain => hostname.endsWith(domain))) {
        return 'staging';
    }

    // Check for local development
    if (LOCAL_INDICATORS.some(indicator => hostname.includes(indicator))) {
        return 'local';
    }

    // Default to local for unknown environments
    console.warn(`⚠️ Unknown hostname "${hostname}", defaulting to local mode`);
    return 'local';
}

/**
 * Get environment override from localStorage
 */
function getLocalStorageOverride(): EnvironmentMode | null {
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }

    const override = localStorage.getItem('asd_environment_mode');
    if (override && ['local', 'mainnet', 'staging'].includes(override)) {
        return override as EnvironmentMode;
    }

    return null;
}

/**
 * Build environment configuration based on detected mode
 */
function buildEnvironment(mode: EnvironmentMode): ICEnvironment {
    switch (mode) {
        case 'mainnet':
            return {
                mode: 'mainnet',
                isMainnet: true,
                useProxy: false,          // canister calls go direct to IC via AgentAdapter
                proxyUrl: 'https://proxy.unboundata.com',  // server-side features via tunnel
                icHost: DEFAULTS.mainnetHost,
                localReplicaHost: '',
                internetIdentityCanisterId: DEFAULTS.mainnetInternetIdentity,
                debug: false
            };

        case 'staging':
            return {
                mode: 'staging',
                isMainnet: false,
                useProxy: false,
                proxyUrl: 'https://proxy.unboundata.com',  // server-side features via tunnel
                icHost: DEFAULTS.stagingHost,
                localReplicaHost: '',
                internetIdentityCanisterId: DEFAULTS.mainnetInternetIdentity,
                debug: true
            };

        case 'local':
        default: {
            // Cloudflare Tunnel: use ProxyAdapter (simple REST) instead of
            // AgentAdapter (HttpAgent), which has issues through CDN proxies.
            const cfDev = isCloudflareDevDomain(getBaseHost());
            return {
                mode: 'local',
                isMainnet: false,
                useProxy: cfDev,
                proxyUrl: DEFAULTS.localProxyUrl,
                icHost: DEFAULTS.localReplicaHost,
                localReplicaHost: DEFAULTS.localReplicaHost,
                internetIdentityCanisterId: DEFAULTS.localInternetIdentity,
                debug: true
            };
        }
    }
}

// ============================================================================
// Singleton Instance
// ============================================================================

/**
 * Detected environment mode
 */
const detectedMode = detectEnvironmentMode();

/**
 * Environment configuration singleton
 */
export const environment: ICEnvironment = buildEnvironment(detectedMode);

// Log environment on load (for debugging)
if (typeof window !== 'undefined') {
    console.log(`🌐 ASD Environment: ${environment.mode.toUpperCase()}`);
    console.log(`   IC Host: ${environment.icHost}`);
    console.log(`   Use Proxy: ${environment.useProxy}`);
    if (environment.proxyUrl) {
        console.log(`   Proxy URL: ${environment.proxyUrl}`);
    }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if running on mainnet
 */
export function isMainnet(): boolean {
    return environment.isMainnet;
}

/**
 * Check if running locally
 */
export function isLocal(): boolean {
    return environment.mode === 'local';
}

/**
 * Check if proxy should be used
 */
export function useProxy(): boolean {
    return environment.useProxy;
}

/**
 * Get the appropriate host URL for IC agent
 */
export function getICHost(): string {
    return environment.icHost;
}

/**
 * Get the proxy URL for server-side features
 */
export function getProxyUrl(): string {
    return environment.proxyUrl;
}

/**
 * Build a proxy endpoint URL
 * @param canisterName - Name of the canister
 * @param methodName - Method to call
 */
export function buildProxyUrl(canisterName: string, methodName: string): string {
    return `${environment.proxyUrl}/${canisterName}/${methodName}`;
}

/**
 * Build a proxy endpoint URL with /call/ prefix
 * @param canisterName - Name of the canister
 * @param methodName - Method to call
 */
export function buildProxyCallUrl(canisterName: string, methodName: string): string {
    return `${environment.proxyUrl}/call/${canisterName}/${methodName}`;
}

// ============================================================================
// Testing Utilities
// ============================================================================

/**
 * Override the environment mode (for testing)
 * Requires page reload to take effect
 */
export function setEnvironmentOverride(mode: EnvironmentMode | null): void {
    if (typeof window === 'undefined' || !window.localStorage) {
        console.warn('localStorage not available');
        return;
    }

    if (mode === null) {
        localStorage.removeItem('asd_environment_mode');
        console.log('🌐 Environment override removed. Reload page to apply.');
    } else {
        localStorage.setItem('asd_environment_mode', mode);
        console.log(`🌐 Environment override set to "${mode}". Reload page to apply.`);
    }
}

/**
 * Clear environment override
 */
export function clearEnvironmentOverride(): void {
    setEnvironmentOverride(null);
}

// Expose testing utilities on window for console access
if (typeof window !== 'undefined') {
    (window as any).asdEnvironment = {
        current: environment,
        isMainnet,
        isLocal,
        setOverride: setEnvironmentOverride,
        clearOverride: clearEnvironmentOverride
    };
}
